import { useEffect, useRef } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import type { Page } from '@fsm/shared';
import { Button, Spinner } from './ui';

/**
 * A list that does not stop without saying so.
 *
 * ## The failure this exists for
 *
 * Every list screen asked the API for one page — a hundred customers, a
 * hundred jobs, twenty-five estimates — and then rendered it as though that
 * were everything. The API could page; it returned a cursor; nothing in the
 * web app ever used one.
 *
 * So a contractor who imported 878 customers opened the screen, counted a
 * hundred, and reasonably concluded the import had failed. The data was all
 * there. The screen was lying, and the subtitle underneath it — "100 on
 * file" — was lying with more confidence than anything else on the page.
 *
 * That is the worst kind of bug in a system of record: not wrong data, but a
 * true number labelled as a different true number.
 *
 * ## What this does about it
 *
 * Pages are fetched as the list is scrolled, so in normal use everything is
 * simply there. The button underneath is not decoration: an observer that
 * never fires — an unusual browser, a short list in a tall window, a
 * server-rendered frame — would otherwise strand the remaining rows with no
 * way to ask for them. One of the two always works.
 *
 * And the foot always states what is loaded against the total, because a
 * list that has not finished loading should say so rather than look finished.
 */
export function usePagedList<T>({
  queryKey,
  fetchPage,
  enabled = true,
  pageSize = 100,
}: {
  queryKey: readonly unknown[];
  fetchPage: (cursor: string | undefined, limit: number) => Promise<Page<T>>;
  enabled?: boolean;
  pageSize?: number;
}) {
  const query = useInfiniteQuery({
    queryKey,
    enabled,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => fetchPage(pageParam, pageSize),
    getNextPageParam: (last) => last.nextCursor,
    placeholderData: (prev) => prev,
  });

  const pages = query.data?.pages ?? [];
  const items = pages.flatMap((p) => p.items);

  return {
    items,
    /* How many match, which is not how many are loaded. */
    total: pages[0]?.total ?? items.length,
    isLoading: query.isLoading,
    hasMore: Boolean(query.hasNextPage),
    loadingMore: query.isFetchingNextPage,
    loadMore: () => {
      if (query.hasNextPage && !query.isFetchingNextPage) void query.fetchNextPage();
    },
  };
}

export type PagedList<T> = ReturnType<typeof usePagedList<T>>;

/**
 * The foot of the list: what is loaded, out of how many, and a way to get on.
 *
 * Rendered even when everything is loaded, because "878 of 878" is the
 * sentence that tells somebody the list is complete — and the absence of a
 * sentence is what made the old lists feel complete when they were not.
 */
export function ListFoot<T>({
  list,
  one,
  many,
}: {
  list: PagedList<T>;
  one: string;
  many: string;
}) {
  const sentinel = useRef<HTMLDivElement>(null);

  /*
   * Fetch the next page when the foot comes into view. The root is left as
   * the viewport rather than named: each list screen has its own scrolling
   * element, and an observer given the wrong root never fires at all — which
   * fails silently, in exactly the way this component exists to prevent. The
   * button below is the backstop.
   */
  useEffect(() => {
    const node = sentinel.current;
    if (!node || !list.hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) list.loadMore();
      },
      { rootMargin: '400px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [list.hasMore, list.loadMore, list]);

  if (list.total === 0) return null;

  return (
    <div
      ref={sentinel}
      className="flex items-center justify-center gap-3 border-t border-line px-4 py-3"
    >
      <span className="text-[12.5px] text-ink-3">
        {list.hasMore
          ? `Showing ${list.items.length.toLocaleString()} of ${list.total.toLocaleString()}`
          : `${list.total.toLocaleString()} ${list.total === 1 ? one : many}`}
      </span>
      {list.hasMore && (
        <Button size="sm" loading={list.loadingMore} onClick={list.loadMore}>
          Load more
        </Button>
      )}
      {list.loadingMore && <Spinner className="size-4 text-ink-4" />}
    </div>
  );
}
